import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {fetchTVShowDetails} from "../utilities/dataSources";

/**
 * Standalone programme details screen opened from a shared deep link:
 * `/programme/:country/:eventId`. Fetches the details by event ID and shows
 * the programme plus its image, with a close button that returns to the
 * main TV guide.
 */
export function ProgrammeDetailsPage() {
    const {country, eventId} = useParams();
    const navigate = useNavigate();

    const isNigeria = country === "nigeria";
    const countryName: "south-africa" | "nigeria" = isNigeria ? "nigeria" : "south-africa";

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<any>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetchTVShowDetails(countryName, eventId || "")
            .then((response) => {
                if (cancelled) return;
                if (!response.data?.Duration) {
                    throw new Error("No details available for this programme.");
                }
                setData(response.data);
            })
            .catch((err) => {
                console.error(err);
                if (cancelled) return;
                alert("Programme details could not be loaded.");
                navigate(`/${countryName}`, {replace: true});
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [countryName, eventId, navigate]);

    const goBackToGuide = () => {
        navigate(`/${countryName}`);
    };

    if (loading) {
        return (
            <div className="App">
                <main className="content">
                    <div className="pc-spinner" />
                </main>
            </div>
        );
    }

    const hiddenItems = [
        "GlobalID",
        "GlobalSeasonID",
        "GlobalSeriesID",
        "Source Key ID",
    ];
    const keyWords: { Name: string; Value: string }[] = data?.Keywords || [];

    return (
        <div className="App">
            <main className="content">
                <div className="programme-details-card">
                    <button
                        className="programme-details-close"
                        onClick={goBackToGuide}
                        aria-label="Close"
                    >
                        ✕
                    </button>
                    <h2>{data?.Title}</h2>
                    <img
                        src={data?.ThumbnailUri}
                        alt={data?.Title}
                        className="programme-details-image"
                    />
                    {data?.Channel && (
                        <p><strong>Channel:</strong> {data.Channel}</p>
                    )}
                    {data?.Duration && (
                        <p><strong>Duration:</strong> {data.Duration}</p>
                    )}
                    {data?.Rating && (
                        <p><strong>Rating:</strong> {data.Rating}</p>
                    )}
                    {data?.Synopsis && (
                        <p><strong>Synopsis:</strong> {data.Synopsis}</p>
                    )}

                    <ul className="programme-details-items">
                        {data?.Items
                            ?.filter((item: any) => !hiddenItems.includes(item.Key))
                            .map((item: any, index: number) => (
                                <li key={index}>
                                    <strong>{item.Key}:</strong> {item.Value}
                                </li>
                            ))}
                    </ul>

                    <div>
                        {keyWords
                            .filter((item) => item.Value)
                            .map((word, index) => (
                                <div key={index} className="keyword-pill">
                                    {word.Value}
                                </div>
                            ))}
                    </div>

                    <button
                        className="modal-btn programme-details-back"
                        onClick={goBackToGuide}
                    >
                        Back to TV guide
                    </button>
                </div>
            </main>
        </div>
    );
}
